import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Ban,
  Check,
  ClipboardList,
  Eye,
  MessageSquarePlus,
  MoreHorizontal,
  Pause,
  Play,
  Plus,
  Target,
  X,
} from "lucide-react";

import {
  APIError,
  approveMissionPlan,
  cancelMission,
  createMission,
  getMission,
  listChannels,
  listMissions,
  pauseMission,
  rejectMissionPlan,
  resumeMission,
  steerMission,
  type Channel,
  type Mission,
  type MissionAutonomy,
  type MissionDetailView,
  type MissionStatus,
} from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/Page";
import { useToast } from "@/components/ui/toast";
import { useConfirm } from "@/components/ui/confirm";
import { cn } from "@/lib/utils";

const is403 = (e: unknown) => e instanceof APIError && e.status === 403;
const is402 = (e: unknown) => e instanceof APIError && e.status === 402;

// missionErr surfaces the server's specific validation sentence (a user-safe 4xx
// message) while keeping the friendly generic handling for auth/5xx.
function missionErr(e: unknown, fallback: string): string {
  if (is402(e) && e instanceof APIError && e.message) return e.message; // plan-limit upgrade copy
  if (e instanceof APIError && e.status >= 400 && e.status < 500 && e.status !== 401 && e.status !== 403 && e.message) {
    return e.message;
  }
  return humanizeError(e, fallback);
}

const STATUS_CLS: Record<MissionStatus, string> = {
  planning: "border-primary/30 bg-primary/10 text-primary",
  awaiting_plan_approval: "border-warning/40 bg-warning/10 text-warning",
  active: "border-success/30 bg-success/10 text-success",
  ticking: "border-primary/30 bg-primary/10 text-primary",
  blocked: "border-warning/40 bg-warning/10 text-warning",
  paused: "border-warning/40 bg-warning/10 text-warning",
  done: "border-success/30 bg-success/10 text-success",
  failed: "border-destructive/40 bg-destructive/10 text-destructive",
  cancelled: "border-border bg-muted text-muted-foreground",
};

const STATUS_LABEL: Record<MissionStatus, string> = {
  planning: "Planning",
  awaiting_plan_approval: "Plan needs approval",
  active: "Active",
  ticking: "Working",
  blocked: "Awaiting approval",
  paused: "Paused",
  done: "Done",
  failed: "Failed",
  cancelled: "Cancelled",
};

const AUTONOMY_LABEL: Record<MissionAutonomy, string> = {
  ask_first: "Ask first",
  checkpoints: "Checkpoints",
  autonomous: "Autonomous",
};

const TERMINAL: MissionStatus[] = ["done", "failed", "cancelled"];

const ucToCredits = (uc: number) => uc / 1_000_000;
function creditsLabel(uc: number): string {
  const c = ucToCredits(uc);
  return c >= 100 ? Math.round(c).toLocaleString() : c.toFixed(c < 10 ? 2 : 1);
}

function createdByLabel(by: string): string {
  if (by === "api-key") return "API";
  return "you";
}

function fmtWhen(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

// BudgetBar shows spend against the per-mission ceiling.
function BudgetBar({ spentUC, budgetUC }: { spentUC: number; budgetUC: number }) {
  const pct = budgetUC > 0 ? Math.min(100, Math.round((spentUC / budgetUC) * 100)) : 0;
  const tone = pct >= 90 ? "bg-destructive" : pct >= 70 ? "bg-warning" : "bg-primary";
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {creditsLabel(spentUC)} / {budgetUC > 0 ? creditsLabel(budgetUC) : "∞"} credits
        </span>
        {budgetUC > 0 && <span>{pct}%</span>}
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className={cn("h-full rounded-full transition-all", tone)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function MissionsTab({ projectId }: { projectId: string }) {
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const toast = useToast();
  const confirm = useConfirm();

  const refresh = useCallback(async () => {
    try {
      setMissions(await listMissions(projectId));
      setErr(null);
    } catch (e) {
      setErr(missionErr(e, "Couldn't load missions"));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Missions tick in the background — keep the list fresh while the tab is open.
  useEffect(() => {
    const t = window.setInterval(() => void refresh(), 10_000);
    return () => window.clearInterval(t);
  }, [refresh]);

  const apply = useCallback((m: Mission) => {
    setMissions((cur) => cur.map((x) => (x.id === m.id ? m : x)));
  }, []);

  const act = useCallback(
    async (fn: () => Promise<Mission>, okMsg: string) => {
      try {
        apply(await fn());
        toast.success(okMsg);
      } catch (e) {
        toast.error(is403(e) ? "You need editor access to manage missions." : missionErr(e, "Couldn't update the mission"));
      }
    },
    [apply, toast],
  );

  const onApprove = useCallback((m: Mission) => act(() => approveMissionPlan(projectId, m.id), "Plan approved — the agent is on it."), [act, projectId]);
  const onPause = useCallback((m: Mission) => act(() => pauseMission(projectId, m.id), "Mission paused."), [act, projectId]);
  const onResume = useCallback((m: Mission) => act(() => resumeMission(projectId, m.id), "Mission resumed."), [act, projectId]);

  const onReject = useCallback(
    async (m: Mission) => {
      const reason = window.prompt("Why send the plan back? (optional — the agent re-plans with this feedback)") ?? undefined;
      await act(() => rejectMissionPlan(projectId, m.id, reason), "Plan rejected — the agent will re-plan.");
    },
    [act, projectId],
  );

  const onCancel = useCallback(
    async (m: Mission) => {
      const ok = await confirm({
        title: "Cancel this mission?",
        description: "It stops for good. This can't be undone.",
        confirmText: "Cancel mission",
        destructive: true,
      });
      if (!ok) return;
      try {
        await cancelMission(projectId, m.id);
        setMissions((cur) => cur.map((x) => (x.id === m.id ? { ...x, status: "cancelled" as MissionStatus } : x)));
        toast.success("Mission cancelled.");
      } catch (e) {
        toast.error(is403(e) ? "You need editor access to manage missions." : missionErr(e, "Couldn't cancel"));
      }
    },
    [confirm, projectId, toast],
  );

  const { active, history } = useMemo(() => {
    const a: Mission[] = [];
    const h: Mission[] = [];
    for (const m of missions) (TERMINAL.includes(m.status) ? h : a).push(m);
    return { active: a, history: h };
  }, [missions]);

  const rowActions = { onApprove, onReject, onPause, onResume, onCancel, onView: (m: Mission) => setDetailId(m.id) };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Missions</CardTitle>
          <CardDescription>
            Long-running objectives the agent plans and runs on its own over many steps. <span className="font-medium text-foreground">Just ask the agent in chat</span>{" "}
            (e.g. <span className="italic">"over the next week, research our competitors and draft a report"</span>) and it
            starts a mission. Watch, approve the plan, steer, or pause it here. Spend is capped per mission.
          </CardDescription>
        </div>
        <Button size="sm" variant="outline" onClick={() => setCreating(true)} title="Start a mission with explicit budget/autonomy (you can also just ask in chat)">
          <Plus className="h-4 w-4" /> New mission
        </Button>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Spinner /> Loading…
          </div>
        ) : err ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
            <span className="text-destructive">{err}</span>
            <Button variant="outline" size="sm" onClick={() => void refresh()}>
              Retry
            </Button>
          </div>
        ) : missions.length === 0 ? (
          <div className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground">
            <Target className="h-6 w-6 opacity-50" />
            <span>
              No missions yet. <span className="font-medium text-foreground">Ask the agent in chat</span> to take on a
              long objective and it'll start one here — or use <span className="font-medium text-foreground">New mission</span> to
              set budget and autonomy explicitly.
            </span>
          </div>
        ) : (
          <>
            <MissionList missions={active} {...rowActions} />
            {history.length > 0 && (
              <div className="space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">History</div>
                <MissionList missions={history} {...rowActions} />
              </div>
            )}
          </>
        )}
      </CardContent>

      {creating && (
        <MissionCreateDialog
          projectId={projectId}
          onClose={() => setCreating(false)}
          onCreated={(m) => {
            setMissions((cur) => [m, ...cur]);
            setCreating(false);
            setDetailId(m.id);
          }}
        />
      )}
      {detailId && (
        <MissionDetailDialog
          projectId={projectId}
          missionId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={(m) => apply(m)}
        />
      )}
    </Card>
  );
}

type RowActions = {
  onApprove: (m: Mission) => void;
  onReject: (m: Mission) => void;
  onPause: (m: Mission) => void;
  onResume: (m: Mission) => void;
  onCancel: (m: Mission) => void;
  onView: (m: Mission) => void;
};

function MissionList({ missions, ...a }: { missions: Mission[] } & RowActions) {
  return (
    <ul className="divide-y rounded-md border">
      {missions.map((m) => {
        const awaiting = m.status === "awaiting_plan_approval";
        const pausable = m.status === "active" || m.status === "ticking";
        const resumable = m.status === "paused";
        const cancellable = !TERMINAL.includes(m.status);
        const title = m.title?.trim() || m.objective;
        return (
          <li key={m.id} className="flex items-start justify-between gap-3 px-4 py-3">
            <button className="flex min-w-0 flex-1 items-start gap-3 text-left" onClick={() => a.onView(m)}>
              <Badge variant="outline" className={cn("mt-0.5 shrink-0 text-[11px]", STATUS_CLS[m.status])}>
                {STATUS_LABEL[m.status]}
              </Badge>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{title}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {AUTONOMY_LABEL[m.autonomy] ?? m.autonomy} · tick {m.ticks_used}
                  {m.tick_cap > 0 ? `/${m.tick_cap}` : ""} · by {createdByLabel(m.created_by)} · {fmtWhen(m.last_activity_at ?? m.created_at)}
                  {m.pause_reason && <span className="text-warning"> · {m.pause_reason}</span>}
                </div>
                <div className="mt-1.5 max-w-xs">
                  <BudgetBar spentUC={m.spent_uc} budgetUC={m.budget_uc} />
                </div>
              </div>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Mission actions">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => a.onView(m)}>
                  <Eye /> View
                </DropdownMenuItem>
                {awaiting && (
                  <>
                    <DropdownMenuItem onSelect={() => a.onApprove(m)}>
                      <Check /> Approve plan
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => a.onReject(m)}>
                      <X /> Reject plan
                    </DropdownMenuItem>
                  </>
                )}
                {pausable && (
                  <DropdownMenuItem onSelect={() => a.onPause(m)}>
                    <Pause /> Pause
                  </DropdownMenuItem>
                )}
                {resumable && (
                  <DropdownMenuItem onSelect={() => a.onResume(m)}>
                    <Play /> Resume
                  </DropdownMenuItem>
                )}
                {cancellable && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => a.onCancel(m)} className="text-destructive focus:text-destructive">
                      <Ban /> Cancel
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </li>
        );
      })}
    </ul>
  );
}

const inputCls = "w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

function MissionCreateDialog({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: string;
  onClose: () => void;
  onCreated: (m: Mission) => void;
}) {
  const [title, setTitle] = useState("");
  const [objective, setObjective] = useState("");
  const [criteria, setCriteria] = useState("");
  const [autonomy, setAutonomy] = useState<MissionAutonomy>("checkpoints");
  const [budget, setBudget] = useState("");
  const [channelId, setChannelId] = useState("");
  const [channels, setChannels] = useState<Channel[]>([]);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    // Agent-role channels are an optional heads-up target. Admin-gated → an editor
    // may 403; degrade to "no channels" (dashboard-only notifications).
    listChannels(projectId)
      .then((r) => setChannels(r.channels.filter((c) => c.role === "agent" && c.enabled)))
      .catch(() => setChannels([]));
  }, [projectId]);

  const onSave = useCallback(async () => {
    if (!objective.trim()) {
      toast.error("Give the mission an objective.");
      return;
    }
    setSaving(true);
    try {
      const m = await createMission(projectId, {
        title: title.trim() || undefined,
        objective: objective.trim(),
        success_criteria: criteria.trim() || undefined,
        autonomy,
        budget_credits: budget.trim() ? Number(budget) : undefined,
        channel_id: channelId || undefined,
      });
      onCreated(m);
      toast.success("Mission created — the agent is planning it now.");
    } catch (e) {
      toast.error(is403(e) ? "You need editor access to create missions." : missionErr(e, "Couldn't create the mission"));
    } finally {
      setSaving(false);
    }
  }, [projectId, title, objective, criteria, autonomy, budget, channelId, onCreated, toast]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New mission</DialogTitle>
          <DialogDescription>
            Describe the outcome you want. The agent breaks it into a plan and executes it over many steps.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] space-y-4 overflow-y-auto px-0.5">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Title (optional)</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="e.g. Weekly competitor digest" />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Objective</label>
            <textarea
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              rows={4}
              className={cn(inputCls, "resize-y")}
              placeholder="What should the agent accomplish? Be concrete about the goal and scope."
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Success criteria (optional)</label>
            <textarea
              value={criteria}
              onChange={(e) => setCriteria(e.target.value)}
              rows={2}
              className={cn(inputCls, "resize-y")}
              placeholder="How will you know it's done? The agent checks its work against this."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Autonomy</label>
              <select value={autonomy} onChange={(e) => setAutonomy(e.target.value as MissionAutonomy)} className={inputCls}>
                <option value="ask_first">Ask first — approve the plan + risky steps</option>
                <option value="checkpoints">Checkpoints — approve the plan, then run</option>
                <option value="autonomous">Autonomous — run without a plan gate</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Budget (credits, optional)</label>
              <input
                type="number"
                min={0}
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="blank = no limit"
                className={inputCls}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Notify me on</label>
            <select value={channelId} onChange={(e) => setChannelId(e.target.value)} className={inputCls}>
              <option value="">Dashboard only</option>
              {channels.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.kind})
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Get a message when the plan needs approval, the mission pauses, or it finishes. Otherwise it's visible only
              here in the Missions tab.
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            By default a mission has no spending limit of its own — it draws on your workspace credits like everything
            else. Set a budget only if you want a cap for this mission. Irreversible actions still go through your policy
            rules even on Autonomous.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void onSave()} disabled={saving}>
            {saving ? "Creating…" : "Create mission"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TASK_CLS: Record<string, string> = {
  pending: "border-border bg-muted text-muted-foreground",
  doing: "border-primary/30 bg-primary/10 text-primary",
  blocked: "border-warning/40 bg-warning/10 text-warning",
  done: "border-success/30 bg-success/10 text-success",
  failed: "border-destructive/40 bg-destructive/10 text-destructive",
};

function MissionDetailDialog({
  projectId,
  missionId,
  onClose,
  onChanged,
}: {
  projectId: string;
  missionId: string;
  onClose: () => void;
  onChanged: (m: Mission) => void;
}) {
  const [detail, setDetail] = useState<MissionDetailView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [steer, setSteer] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const confirm = useConfirm();

  const load = useCallback(async () => {
    try {
      setDetail(await getMission(projectId, missionId));
      setErr(null);
    } catch (e) {
      setErr(missionErr(e, "Couldn't load the mission"));
    }
  }, [projectId, missionId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live view: refetch the ledger/timeline/budget while the dialog is open (a tick
  // runs every few seconds). Skip a poll while an action is in flight.
  useEffect(() => {
    const t = window.setInterval(() => {
      if (!busy) void load();
    }, 4_000);
    return () => window.clearInterval(t);
  }, [load, busy]);

  const run = useCallback(
    async (fn: () => Promise<Mission>, okMsg: string) => {
      setBusy(true);
      try {
        const m = await fn();
        onChanged(m);
        toast.success(okMsg);
        await load();
      } catch (e) {
        toast.error(is403(e) ? "You need editor access to manage missions." : missionErr(e, "Couldn't update"));
      } finally {
        setBusy(false);
      }
    },
    [onChanged, toast, load],
  );

  const onSteer = useCallback(async () => {
    const msg = steer.trim();
    if (!msg) return;
    setBusy(true);
    try {
      await steerMission(projectId, missionId, msg);
      setSteer("");
      toast.success("Sent — the agent will read it on its next step.");
      await load();
    } catch (e) {
      toast.error(is403(e) ? "You need editor access." : missionErr(e, "Couldn't send"));
    } finally {
      setBusy(false);
    }
  }, [steer, projectId, missionId, toast, load]);

  const m = detail?.mission;
  const awaiting = m?.status === "awaiting_plan_approval";
  const pausable = m?.status === "active" || m?.status === "ticking";
  const resumable = m?.status === "paused";
  const steerable = m ? !TERMINAL.includes(m.status) && m.status !== "awaiting_plan_approval" : false;
  const cancellable = m ? !TERMINAL.includes(m.status) : false;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        {!m ? (
          <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
            {err ? <span className="text-destructive">{err}</span> : <><Spinner /> Loading…</>}
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={cn("text-[11px]", STATUS_CLS[m.status])}>
                  {STATUS_LABEL[m.status]}
                </Badge>
                <DialogTitle className="truncate">{m.title?.trim() || "Mission"}</DialogTitle>
              </div>
              <DialogDescription className="whitespace-pre-wrap pt-1">{m.objective}</DialogDescription>
            </DialogHeader>

            <div className="max-h-[60vh] space-y-4 overflow-y-auto px-0.5">
              {m.success_criteria && (
                <div className="rounded-md border bg-muted/30 p-3 text-sm">
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Success criteria</div>
                  <div className="whitespace-pre-wrap text-muted-foreground">{m.success_criteria}</div>
                </div>
              )}

              <BudgetBar spentUC={m.spent_uc} budgetUC={m.budget_uc} />

              <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                <div><span className="text-foreground">{AUTONOMY_LABEL[m.autonomy] ?? m.autonomy}</span><br />autonomy</div>
                <div><span className="text-foreground">{m.phase || "plan"}</span><br />phase</div>
                <div><span className="text-foreground">{m.ticks_used}{m.tick_cap > 0 ? `/${m.tick_cap}` : ""}</span><br />ticks</div>
              </div>

              {m.status === "blocked" && m.held_tool_name && (
                <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-sm">
                  Waiting on approval for <span className="font-medium">{m.held_tool_name}</span>. Approve or reject it in
                  the agent's pending queue (Channels / Policy); the mission resumes automatically.
                </div>
              )}

              {/* Plan ledger */}
              <div>
                <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <ClipboardList className="h-3.5 w-3.5" /> Plan
                </div>
                {detail.tasks.length === 0 ? (
                  <div className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
                    No plan yet — the agent is still planning.
                  </div>
                ) : (
                  <ul className="divide-y rounded-md border">
                    {detail.tasks.map((t) => (
                      <li key={t.id} className="flex items-start gap-2 px-3 py-2">
                        <Badge variant="outline" className={cn("mt-0.5 shrink-0 text-[10px] capitalize", TASK_CLS[t.status] ?? TASK_CLS.pending)}>
                          {t.status}
                        </Badge>
                        <div className="min-w-0 text-sm">
                          <span>{t.description}</span>
                          {t.result_ref && <span className="text-xs text-muted-foreground"> → {t.result_ref}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Timeline */}
              {detail.events.length > 0 && (
                <div>
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Timeline</div>
                  <ul className="space-y-1.5 text-xs">
                    {detail.events.map((ev) => (
                      <li key={ev.id} className="flex items-baseline gap-2">
                        <span className="shrink-0 tabular-nums text-muted-foreground">{fmtWhen(ev.ts)}</span>
                        <span className="text-muted-foreground">{ev.summary || ev.kind.replace(/_/g, " ")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Steer */}
              {steerable && (
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium">
                    <MessageSquarePlus className="h-4 w-4" /> Steer the agent
                  </label>
                  <textarea
                    value={steer}
                    onChange={(e) => setSteer(e.target.value)}
                    rows={2}
                    className={cn(inputCls, "resize-y")}
                    placeholder="Redirect or add guidance — the agent reads this on its next step."
                  />
                  <div className="flex justify-end">
                    <Button size="sm" variant="outline" onClick={() => void onSteer()} disabled={busy || !steer.trim()}>
                      Send guidance
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="flex-wrap gap-2">
              {awaiting && (
                <>
                  <Button variant="outline" onClick={() => void run(() => rejectMissionPlan(projectId, m.id, window.prompt("Why send it back? (optional)") ?? undefined), "Plan rejected — re-planning.")} disabled={busy}>
                    <X className="h-4 w-4" /> Reject plan
                  </Button>
                  <Button onClick={() => void run(() => approveMissionPlan(projectId, m.id), "Plan approved.")} disabled={busy}>
                    <Check className="h-4 w-4" /> Approve plan
                  </Button>
                </>
              )}
              {pausable && (
                <Button variant="outline" onClick={() => void run(() => pauseMission(projectId, m.id), "Paused.")} disabled={busy}>
                  <Pause className="h-4 w-4" /> Pause
                </Button>
              )}
              {resumable && (
                <Button onClick={() => void run(() => resumeMission(projectId, m.id), "Resumed.")} disabled={busy}>
                  <Play className="h-4 w-4" /> Resume
                </Button>
              )}
              {cancellable && (
                <Button
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  disabled={busy}
                  onClick={async () => {
                    const ok = await confirm({
                      title: "Cancel this mission?",
                      description: "It stops for good. This can't be undone.",
                      confirmText: "Cancel mission",
                      destructive: true,
                    });
                    if (ok) await run(() => cancelMission(projectId, m.id), "Mission cancelled.");
                  }}
                >
                  <Ban className="h-4 w-4" /> Cancel mission
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
